import React, { useState } from 'react';
import styled from 'styled-components';
import Desktop from './components/Desktop';
import Dock from './components/Dock';
import Window from './components/Window';
import MenuBar from './components/MenuBar';
import StickyNote from './components/StickyNote';
import MessagesApp from './components/apps/MessagesApp';
import SpotifyApp from './components/apps/AppleMusicApp';
import AboutMeApp from './components/apps/AboutMeApp';
import FinderApp from './components/apps/FinderApp';
import TrashApp from './components/apps/TrashApp';
import ImagePreviewApp from './components/apps/ImagePreviewApp';
import TextEditorApp from './components/apps/TextEditorApp';
import TextFileApp from './components/apps/TextFileApp';

const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow: hidden;
`;

const App = () => {
  const [openWindows, setOpenWindows] = useState([]);
  const [activeWindow, setActiveWindow] = useState(null);

  const openWindow = (appType, title, props = {}) => {
    console.log('Opening window:', appType, title, props);
    const newWindow = {
      id: Date.now(),
      type: appType,
      title,
      props,
      position: { x: 100 + openWindows.length * 50, y: 100 + openWindows.length * 50 },
      size: { width: 600, height: 400 },
      isMinimized: false
    };
    
    setOpenWindows(prev => {
      console.log('Previous windows:', prev);
      const updated = [...prev, newWindow];
      console.log('Updated windows:', updated);
      return updated;
    });
    setActiveWindow(newWindow.id);
  };

  const closeWindow = (windowId) => {
    setOpenWindows(prev => prev.filter(window => window.id !== windowId));
    if (activeWindow === windowId) {
      setActiveWindow(null);
    }
  };

  const minimizeWindow = (windowId) => {
    setOpenWindows(prev => 
      prev.map(window => 
        window.id === windowId 
          ? { ...window, isMinimized: !window.isMinimized }
          : window
      )
    );
  };

  const setWindowActive = (windowId) => {
    setActiveWindow(windowId);
    // Bring window to front
    setOpenWindows(prev => [
      ...prev.filter(window => window.id !== windowId),
      prev.find(window => window.id === windowId)
    ]);
  };

  const updateWindowPosition = (windowId, position) => {
    setOpenWindows(prev => 
      prev.map(window => 
        window.id === windowId 
          ? { ...window, position }
          : window
      )
    );
  };

  const updateWindowSize = (windowId, size) => {
    setOpenWindows(prev => 
      prev.map(window => 
        window.id === windowId 
          ? { ...window, size }
          : window
      )
    );
  };

  const handleDesktopClick = () => {
    // Close all windows when clicking on desktop
    setOpenWindows([]);
    setActiveWindow(null);
  };

  const handleOpenTrash = () => {
    openWindow('trash', 'Trash', {});
  };

  const handleOpenAboutMe = () => {
    // Open multiple windows like in the reference
    const baseTime = Date.now();
    
    // Left image preview window
    openWindow('image', 'IMGlol41.heic', { 
      variant: 'left',
      position: { x: 50, y: 50 },
      size: { width: 400, height: 500 }
    });
    
    // Right image preview window
    setTimeout(() => {
      openWindow('image', 'IMGlol41.heic', { 
        variant: 'right',
        position: { x: 500, y: 50 },
        size: { width: 400, height: 500 }
      });
    }, 100);
    
    // Text editor window (centered, overlapping)
    setTimeout(() => {
      openWindow('texteditor', 'aboutme.txt', { 
        position: { x: 250, y: 200 },
        size: { width: 500, height: 400 }
      });
    }, 200);
  };

  const openTextFile = (fileName, content) => {
    // Calculate position to open in front of other windows
    const centerX = window.innerWidth / 2 - 250;
    const centerY = window.innerHeight / 2 - 200;
    
    openWindow('textfile', fileName, { 
      fileName,
      content,
      position: { x: centerX, y: centerY },
      size: { width: 500, height: 400 },
      zIndex: 200 // Higher z-index to ensure it opens in front
    });
  };

  const renderWindow = (window) => {
    const { id, type, title, props, position, size, isMinimized } = window;
    
    console.log('Rendering window:', { id, type, title, isMinimized });
    
    if (isMinimized) return null;

    const windowProps = {
      id,
      title,
      position: props.position || position,
      size: props.size || size,
      isActive: activeWindow === id,
      onClose: () => closeWindow(id),
      onMinimize: () => minimizeWindow(id),
      onActivate: () => setWindowActive(id),
      onPositionChange: (newPosition) => updateWindowPosition(id, newPosition),
      onSizeChange: (newSize) => updateWindowSize(id, newSize),
      ...props
    };

    console.log('Window props:', windowProps);

    switch (type) {
      case 'messages':
        return <MessagesApp key={id} {...windowProps} />;
      case 'music':
        return <SpotifyApp key={id} {...windowProps} />;
      case 'about':
        return <AboutMeApp key={id} {...windowProps} />;
      case 'finder':
        return <FinderApp key={id} {...windowProps} onOpenTextFile={openTextFile} />;
      case 'trash':
        return <TrashApp key={id} {...windowProps} />;
      case 'image':
        return <ImagePreviewApp key={id} {...windowProps} />;
      case 'texteditor':
        return <TextEditorApp key={id} {...windowProps} />;
      case 'textfile':
        return <TextFileApp key={id} {...windowProps} />;
      default:
        return <Window key={id} {...windowProps} />;
    }
  };

  console.log('Current openWindows:', openWindows);

  return (
    <AppContainer>
      <MenuBar />
      <StickyNote />
      <Desktop 
        onDesktopClick={handleDesktopClick} 
        onOpenTrash={handleOpenTrash}
        onOpenAboutMe={handleOpenAboutMe}
      />
      
      {openWindows.map(renderWindow)}
      
      <Dock 
        onAppClick={(appType, title, props) => openWindow(appType, title, props)}
        openWindows={openWindows}
      />
    </AppContainer>
  );
};

export default App;
